import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { Suspense, lazy, useState } from 'react';

import type { Role } from '~/@types/redux';
import type { DtoV2DocumentAssociationSaveDocumentAssociationsRequestV2 } from '~/__generated__/api';
import Button from '~/common/components/Button';
import Oops from '~/common/components/Oops';
import Spinner from '~/common/components/Spinner';
import Text from '~/common/components/Texts/Text';
import { USER_ROLE } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import { useReadOnlyContext } from '~/common/hooks/useReadOnly';
import { useNavigation, useSelectedAlert } from '~/stores/navigationStore';
import type { GutterItem } from '~/types';

import ClassifyFormContent from './ClassifyFormContent';
import PDFViewer from './PDFViewer';
import {
  StyledClassifyBody,
  StyledClassifyFooter,
  StyledClassifyHeader,
  StyledClassifyRightContainer,
} from './StyledComponents/ClassifyRightContainer.styles';
import { CLASSIFICATION } from './constants';
import { type ClassifyFormData, useAppForm } from './hooks/ClassifyFormHook';
import {
  useRemoveDocumentMutationStore,
  useSaveDocumentAssociationsMutation,
} from './hooks/mutations';
import {
  useDocumentAssociationsQuery,
  useDocumentPdfQuery,
  useDocumentTypeAssociationsQuery,
} from './hooks/queries';
import { LOADER_MESSAGE } from './language.json';

const ActionBar = lazy(
  () => import('~/common/components/GlobalLayout/ActionBar/ActionBar'),
);

type ActionType = 'continue' | 'next';

interface ClassifySubmitMeta {
  actionType: ActionType;
}

interface AssociationSelection {
  value: any;
  [key: string]: any;
}

interface Associations {
  borrowerAssociations?: AssociationSelection[];
  assetDocumentAssociations?: AssociationSelection[];
  incomeDocumentAssociations?: AssociationSelection[];
  liabilityDocumentAssociations?: AssociationSelection[];
  propertyAssociations?: AssociationSelection[];
  [key: string]: AssociationSelection[] | undefined;
}

interface NormalizedAssociations {
  borrowerAssociations: any[];
  assetDocumentAssociations: any[];
  incomeDocumentAssociations: any[];
  liabilityDocumentAssociations: any[];
  propertyAssociations: any[];
  [key: string]: any[];
}

type SaveDocumentAssociationsPayload = Omit<
  DtoV2DocumentAssociationSaveDocumentAssociationsRequestV2,
  'obfuscatedLoanIdentifier'
>;

// removes label property (from react-select inputs) and normalizes form data
// for the save API call
function normalizeAssociations(
  associations: Associations,
): NormalizedAssociations {
  let normalizedAssociations: NormalizedAssociations = {
    borrowerAssociations: [],
    assetDocumentAssociations: [],
    incomeDocumentAssociations: [],
    liabilityDocumentAssociations: [],
    propertyAssociations: [],
  };
  for (const [association, associationSelections] of Object.entries(
    associations,
  )) {
    // Skip non-array values (e.g. documentId or null fields set by restore effect)
    // to prevent overwriting the initialized defaults with undefined.
    if (!Array.isArray(associationSelections)) continue;
    normalizedAssociations = {
      ...normalizedAssociations,
      [association]: associationSelections.map((selection) => selection.value),
    };
  }
  return normalizedAssociations;
}

function hasClassificationChanged(
  selectedDocument: GutterItem,
  formData: ClassifyFormData,
): boolean {
  return (
    selectedDocument.additionalReferences.DocumentCategory !==
      formData.documentCategory?.value ||
    +selectedDocument.additionalReferences.DocumentTypeId !==
      formData.documentType?.value
  );
}

const ClassifyRightContainer = () => {
  const { isReadOnly } = useReadOnlyContext();
  const { role: userRole, isUnderwriter } = useUser();
  const enableActionBar = useNavFeatureFlags().IsActionBarEnabled;
  const selectedAlert = useSelectedAlert();

  const {
    mutate: saveDocumentAssociations,
    status: saveDocumentAssociationsAPICallStatus,
  } = useSaveDocumentAssociationsMutation();

  const isGivenRole = (role: Role): boolean => {
    return userRole.includes(role);
  };

  const { status: getDocumentTypesAndCategoriesAPICallStatus } =
    useDocumentTypeAssociationsQuery();

  const isHideDocsAPICallStatusError = useRemoveDocumentMutationStore(
    (s) => s.status === 'error',
  );

  const {
    data: selectedDocumentAssociations,
    status: getDocumentAssociationsAPICallStatus,
  } = useDocumentAssociationsQuery(
    selectedAlert.ruleReference.id,
    +selectedAlert.additionalReferences.DocumentTypeId,
  );

  const {
    data: pdfBlobUrl,
    isError: isPdfQueryError,
    isLoading: isPdfQueryLoading,
  } = useDocumentPdfQuery(selectedAlert.ruleReference.id);

  const isCategoryReadOnlyPermission =
    useNavigation().selectedCategory?.isReadOnlyPermission;

  const [btnClickedName, setBtnClickedName] = useState<ActionType | null>(null);

  const isLoading =
    getDocumentTypesAndCategoriesAPICallStatus === 'loading' ||
    getDocumentAssociationsAPICallStatus === 'loading';

  const isSaving = saveDocumentAssociationsAPICallStatus === 'loading';

  const hasError =
    getDocumentAssociationsAPICallStatus === 'error' ||
    isHideDocsAPICallStatusError ||
    getDocumentTypesAndCategoriesAPICallStatus === 'error';

  const form = useAppForm({
    formId: `classify-form-${selectedAlert.ruleReference.id}`,
    defaultValues: {
      documentCategory: null,
      documentType: null,
    } as ClassifyFormData,
    onSubmit: ({
      value,
      meta,
    }: {
      value: ClassifyFormData;
      meta: ClassifySubmitMeta;
    }) => {
      const { actionType } = meta;
      const userSelectedAssociations = normalizeAssociations(
        omit(value, [
          CLASSIFICATION.docCategory,
          CLASSIFICATION.docType,
        ]) as Associations,
      );

      const classificationChanged = hasClassificationChanged(
        selectedAlert,
        value,
      );
      const associationChanged = !isEqual(selectedDocumentAssociations, {
        documentId: selectedAlert.ruleReference.id,
        ...userSelectedAssociations,
      });

      const savePayload: SaveDocumentAssociationsPayload = {
        classificationChanged,
        documentId: selectedAlert.ruleReference.id,
        documentTypeId: value.documentType?.value as number,
        receivedMailItemId:
          +selectedAlert.additionalReferences.ReceivedMailItemId,
        userId: 0,
        previousDocumentTypeId: Number.parseInt(
          selectedAlert.additionalReferences.DocumentTypeId,
        ),
        ...userSelectedAssociations,
      };

      saveDocumentAssociations({
        actionType,
        associationChanged,
        classificationChanged,
        data: savePayload,
      });
    },
  });

  const handleContinueClick = async () => {
    setBtnClickedName('continue');
    await form.handleSubmit({
      actionType: 'continue',
    } satisfies ClassifySubmitMeta);
  };

  const handleNextClick = async () => {
    setBtnClickedName('next');
    await form.handleSubmit({
      actionType: 'next',
    } satisfies ClassifySubmitMeta);
  };

  const isDisabled =
    (isCategoryReadOnlyPermission ?? false) ||
    isGivenRole(USER_ROLE.ACCOUNT_EXECUTIVE) ||
    (isReadOnly ?? false);

  if (isLoading) {
    return (
      <Spinner
        loadingMessage={LOADER_MESSAGE.DEFAULT}
        data-testid="loadingComponent"
      />
    );
  }

  if (hasError) {
    return <Oops />;
  }

  return (
    <form.AppForm>
      <StyledClassifyRightContainer
        as="form"
        data-testid="classifyRightContainer"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          handleContinueClick();
        }}
        disabled={isDisabled}
      >
        <StyledClassifyHeader as="header">
          <Text as="h2" id="documentTitle">
            {selectedAlert.displayTitle}
          </Text>
        </StyledClassifyHeader>
        <StyledClassifyBody as="section">
          <PDFViewer
            isError={isPdfQueryError}
            isLoading={isPdfQueryLoading}
            pdfBlobUrl={pdfBlobUrl}
          />
          <ClassifyFormContent form={form} />
        </StyledClassifyBody>
        {enableActionBar ? (
          <Suspense fallback={null}>
            <ActionBar
              primaryAction={{
                id: 'continueBtn',
                name: 'Continue',
                isFormSubmit: true,
                isSubmitting: isSaving && btnClickedName === 'continue',
                isDisabled: isGivenRole(USER_ROLE.ACCOUNT_EXECUTIVE),
                handleSubmit: handleContinueClick,
              }}
              secondaryAction={{
                id: 'nextBtn',
                name: 'Next Doc',
                isSubmitting: isSaving && btnClickedName === 'next',
                isDisabled: isGivenRole(USER_ROLE.ACCOUNT_EXECUTIVE),
                onSubmit: handleNextClick,
              }}
              waitForSignalR={false}
            />
          </Suspense>
        ) : (
          <StyledClassifyFooter as="footer">
            {isUnderwriter && (
              <Button
                isPrimary={false}
                id="nextBtn"
                type="button"
                dataTestid="nextBtn"
                mr="1rem"
                onClick={handleNextClick}
                isLoading={isSaving && btnClickedName === 'next'}
                loadingText="Saving..."
                isDisabled={isGivenRole(USER_ROLE.ACCOUNT_EXECUTIVE)}
              >
                Save and Next Document
              </Button>
            )}
            <Button
              isPrimary
              id="continueBtn"
              dataTestid="continueBtn"
              type="submit"
              isLoading={isSaving && btnClickedName === 'continue'}
              loadingText="Saving..."
              isDisabled={isGivenRole(USER_ROLE.ACCOUNT_EXECUTIVE)}
            >
              {isUnderwriter ? 'Save and Continue' : 'Continue'}
            </Button>
          </StyledClassifyFooter>
        )}
      </StyledClassifyRightContainer>
    </form.AppForm>
  );
};

export default ClassifyRightContainer;
